import {
  getMemberAddressLocation,
  updateMemberAddressLocation,
  type MemberAddressLocationResponse,
} from '@urbanik/api-client';
import { CanonicalModal } from '@urbanik/ui';
import { divIcon, type LeafletMouseEvent, type Marker as LeafletMarker } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { ExternalLink, Navigation, Pencil } from 'lucide-react';
import { MapContainer, Marker, TileLayer, useMapEvents } from 'react-leaflet';

import { useEffect, useState } from 'react';
import { apiBase } from './api-base';
type Form = {
  provinceCode: string;
  cantonCode: string;
  parishCode: string;
  neighborhood: string;
  primaryStreet: string;
  secondaryStreet: string;
  houseNumber: string;
  reference: string;
  latitude: string;
  longitude: string;
  accuracyMeters: string;
  captureMethod: 'device' | 'manual';
  capturedAt: string;
  reason: string;
};
const blank: Form = {
  provinceCode: '',
  cantonCode: '',
  parishCode: '',
  neighborhood: '',
  primaryStreet: '',
  secondaryStreet: '',
  houseNumber: '',
  reference: '',
  latitude: '',
  longitude: '',
  accuracyMeters: '',
  captureMethod: 'manual',
  capturedAt: '',
  reason: '',
};
const pinIcon = divIcon({
  className: 'member-map-pin',
  html: '<span aria-hidden="true"></span>',
  iconSize: [32, 40],
  iconAnchor: [16, 40],
});
function MapSelection({
  latitude,
  longitude,
  onSelect,
}: {
  latitude: number;
  longitude: number;
  onSelect: (latitude: number, longitude: number) => void;
}) {
  useMapEvents({
    click: (event: LeafletMouseEvent) => onSelect(event.latlng.lat, event.latlng.lng),
  });
  return (
    <Marker
      position={[latitude, longitude]}
      icon={pinIcon}
      draggable
      eventHandlers={{
        dragend: (event) => {
          const point = (event.target as LeafletMarker).getLatLng();
          onSelect(point.lat, point.lng);
        },
      }}
    />
  );
}
function LocationMap({
  latitude,
  longitude,
  onSelect,
}: {
  latitude: number | null;
  longitude: number | null;
  onSelect: (latitude: number, longitude: number) => void;
}) {
  const lat = latitude ?? -2.170998;
  const lng = longitude ?? -79.922359;
  return (
    <div className="member-location-map" aria-label="Selector de ubicación exacta">
      <MapContainer
        key={`${lat}:${lng}`}
        center={[lat, lng]}
        zoom={latitude === null ? 13 : 17}
        scrollWheelZoom
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapSelection latitude={lat} longitude={lng} onSelect={onSelect} />
      </MapContainer>
      <p>Haz clic en el mapa o arrastra el marcador para ajustar el punto exacto.</p>
    </div>
  );
}
export function MemberAddressLocation({
  memberId,
  canManage,
  embedded = false,
}: {
  memberId: string;
  canManage: boolean;
  embedded?: boolean;
}) {
  const [data, setData] = useState<MemberAddressLocationResponse | null>(null),
    [form, setForm] = useState<Form>(blank),
    [open, setOpen] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [warning, setWarning] = useState('');
  const load = () =>
    getMemberAddressLocation(apiBase, memberId)
      .then(setData)
      .catch((e) =>
        setError(e instanceof Error ? e.message : 'No pudimos cargar dirección y ubicación.'),
      );
  useEffect(() => {
    void load();
  }, [memberId]);
  const edit = () => {
    const a = data?.address,
      l = data?.location;
    setForm({
      provinceCode: a?.provinceCode ?? '',
      cantonCode: a?.cantonCode ?? '',
      parishCode: a?.parishCode ?? '',
      neighborhood: a?.neighborhood ?? '',
      primaryStreet: a?.primaryStreet ?? '',
      secondaryStreet: a?.secondaryStreet ?? '',
      houseNumber: a?.houseNumber ?? '',
      reference: a?.reference ?? '',
      latitude: l ? String(l.latitude) : '',
      longitude: l ? String(l.longitude) : '',
      accuracyMeters:
        l?.accuracyMeters === null || l?.accuracyMeters === undefined
          ? ''
          : String(l.accuracyMeters),
      captureMethod: l?.captureMethod === 'device' ? 'device' : 'manual',
      capturedAt: l?.capturedAt ?? '',
      reason: '',
    });
    setError('');
    setWarning('');
    setOpen(true);
  };
  const field = (key: keyof Form, label: string, required = false) => (
    <label>
      {label}
      {required ? ' *' : ''}
      <input
        className="ui-input"
        required={required}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
      />
    </label>
  );
  const locate = () => {
    if (!navigator.geolocation) {
      setError('Este dispositivo no permite obtener la ubicación.');
      return;
    }
    setBusy(true);
    setError('');
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const accuracy = p.coords.accuracy;
        setForm({
          ...form,
          latitude: String(p.coords.latitude),
          longitude: String(p.coords.longitude),
          accuracyMeters: String(accuracy),
          captureMethod: 'device',
          capturedAt: new Date(p.timestamp).toISOString(),
        });
        setWarning(
          accuracy > 500
            ? 'La precisión supera 500 m. Corrige las coordenadas manualmente antes de guardar.'
            : accuracy > 100
              ? 'La precisión es mayor a 100 m; verifica el punto antes de confirmar.'
              : 'Ubicación obtenida. Verifica los datos antes de guardar.',
        );
        setBusy(false);
      },
      () => {
        setError(
          'No fue posible obtener la ubicación. Revisa el permiso del navegador o ingrésala manualmente.',
        );
        setBusy(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  };
  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const hasCoordinates = form.latitude.trim() !== '' || form.longitude.trim() !== '';
      const result = await updateMemberAddressLocation(apiBase, memberId, {
        expectedAddressVersion: data?.address?.version ?? null,
        expectedLocationVersion: data?.location?.version ?? null,
        address: {
          provinceCode: form.provinceCode || null,
          cantonCode: form.cantonCode || null,
          parishCode: form.parishCode || null,
          neighborhood: form.neighborhood || null,
          primaryStreet: form.primaryStreet,
          secondaryStreet: form.secondaryStreet || null,
          houseNumber: form.houseNumber || null,
          reference: form.reference || null,
        },
        location: hasCoordinates
          ? {
              latitude: Number(form.latitude),
              longitude: Number(form.longitude),
              accuracyMeters: form.accuracyMeters === '' ? null : Number(form.accuracyMeters),
              captureMethod: form.captureMethod,
              capturedAt: form.capturedAt || new Date().toISOString(),
            }
          : null,
        reason: form.reason,
      });
      setData(result);
      setOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No pudimos guardar los cambios.');
    } finally {
      setBusy(false);
    }
  };
  if (!data)
    return (
      <section className="member-profile-expanded">
        <p role={error ? 'alert' : undefined}>{error || 'Cargando dirección y ubicación…'}</p>
      </section>
    );
  const a = data.address,
    l = data.location;
  return (
    <section
      className={`member-profile-expanded member-location-card${embedded ? ' member-admin-component--embedded' : ''}`}
    >
      <div className="member-profile-section-heading">
        <div>
          <p className="kicker">Dirección y ubicación</p>
          <h2>Residencia registrada</h2>
        </div>
        {canManage ? (
          <button
            className="ui-button ui-button--primary"
            onClick={edit}
            aria-label="Editar dirección y ubicación"
          >
            <Pencil aria-hidden="true" />
          </button>
        ) : null}
      </div>
      <dl className="member-profile-summary">
        <div>
          <dt>Dirección</dt>
          <dd>
            {a
              ? [a.primaryStreet, a.secondaryStreet, a.houseNumber].filter(Boolean).join(' · ')
              : 'No registrada'}
          </dd>
        </div>
        <div>
          <dt>Sector</dt>
          <dd>
            {a
              ? [a.neighborhood, a.parishCode, a.cantonCode, a.provinceCode]
                  .filter(Boolean)
                  .join(' · ') || 'No registrado'
              : 'No registrado'}
          </dd>
        </div>
        <div>
          <dt>Referencia</dt>
          <dd>{a?.reference ?? 'No registrada'}</dd>
        </div>
        <div>
          <dt>Ubicación</dt>
          <dd>
            {l ? (
              <>
                {l.latitude.toFixed(6)}, {l.longitude.toFixed(6)}
                <br />
                <small>
                  {l.captureMethod === 'device' ? 'Dispositivo' : 'Ingreso manual'}
                  {l.accuracyMeters !== null ? ` · ±${Math.round(l.accuracyMeters)} m` : ''}
                </small>
              </>
            ) : (
              'No confirmada'
            )}
          </dd>
        </div>
      </dl>
      {l ? (
        <a
          className="ui-button ui-button--primary member-location-link"
          href={l.mapsUrl}
          target="_blank"
          rel="noreferrer"
        >
          <ExternalLink aria-hidden="true" /> Abrir ubicación
        </a>
      ) : null}
      <details className="member-location-history">
        <summary>
          Ver historial ({data.history.addresses.length} direcciones ·{' '}
          {data.history.locations.length} ubicaciones)
        </summary>
        <ul>
          {data.history.addresses.map((x) => (
            <li key={x.id}>
              <strong>{x.primaryStreet}</strong>
              <span>
                Vigente desde {new Date(x.validFrom).toLocaleString('es-EC')}
                {x.validTo ? ` hasta ${new Date(x.validTo).toLocaleString('es-EC')}` : ' · actual'}
              </span>
            </li>
          ))}
        </ul>
      </details>
      <CanonicalModal
        open={open}
        size="large"
        eyebrow="Información sensible"
        title="Editar dirección y ubicación"
        description="Confirma la dirección administrativa y, opcionalmente, una ubicación exacta."
        onClose={() => {
          if (!busy) setOpen(false);
        }}
      >
        <form
          className="member-location-form"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <fieldset>
            <legend>Dirección residencial</legend>
            <div className="member-location-grid">
              {field('provinceCode', 'Provincia')}
              {field('cantonCode', 'Cantón')}
              {field('parishCode', 'Parroquia')}
              {field('neighborhood', 'Barrio o sector')}
              {field('primaryStreet', 'Calle principal', true)}
              {field('secondaryStreet', 'Calle secundaria')}
              {field('houseNumber', 'Número de casa')}
              {field('reference', 'Referencia')}
            </div>
            <p className="member-location-note">
              Los códigos territoriales quedarán preparados para vincularse al catálogo DPA oficial
              ratificado.
            </p>
          </fieldset>
          <fieldset>
            <legend>Ubicación confirmada</legend>
            <button
              type="button"
              className="ui-button ui-button--primary"
              onClick={locate}
              disabled={busy}
            >
              <Navigation aria-hidden="true" /> Usar ubicación del dispositivo
            </button>
            <p className="member-location-note">
              El navegador solicitará permiso solamente al pulsar este botón. También puedes
              ingresar coordenadas manualmente.
            </p>
            <LocationMap
              latitude={
                form.latitude === '' || !Number.isFinite(Number(form.latitude))
                  ? null
                  : Number(form.latitude)
              }
              longitude={
                form.longitude === '' || !Number.isFinite(Number(form.longitude))
                  ? null
                  : Number(form.longitude)
              }
              onSelect={(latitude, longitude) => {
                setForm({
                  ...form,
                  latitude: latitude.toFixed(6),
                  longitude: longitude.toFixed(6),
                  accuracyMeters: '',
                  captureMethod: 'manual',
                  capturedAt: new Date().toISOString(),
                });
                setWarning(
                  'Punto seleccionado manualmente. Confirma que el marcador corresponde a la residencia.',
                );
                setError('');
              }}
            />
            <div className="member-location-grid">
              {field('latitude', 'Latitud')}
              {field('longitude', 'Longitud')}
              {field('accuracyMeters', 'Precisión (metros)')}
            </div>
            {warning ? <p className="member-location-warning">{warning}</p> : null}
          </fieldset>
          <label>
            Motivo del cambio *
            <textarea
              required
              minLength={10}
              maxLength={500}
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
            />
          </label>
          {error ? (
            <p role="alert" className="member-status-error">
              {error}
            </p>
          ) : null}
          <div className="member-status-actions">
            <button
              type="button"
              className="ui-button"
              onClick={() => setOpen(false)}
              disabled={busy}
            >
              Cancelar
            </button>
            <button className="ui-button ui-button--primary" disabled={busy}>
              {busy ? 'Guardando…' : 'Confirmar cambios'}
            </button>
          </div>
        </form>
      </CanonicalModal>
    </section>
  );
}
